/**
 * Utility for parsing raw log content from S3 storage
 *
 * Log format from worker's build.log:
 * - stdout: "2026-01-21T12:40:45.384Z content"
 * - stderr: "2026-01-21T12:40:45.384Z [stderr] content"
 *
 * Log format from master's chunks (also supported):
 * - "2026-01-21T12:40:45.384Z [stdout] content"
 * - "2026-01-21T12:40:45.384Z [stderr] content"
 */

import type { LogLine } from './stageParser';

// Regex for format with explicit stream marker: "timestamp [stdout|stderr] content"
const LOG_LINE_WITH_MARKER = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z) \[(stdout|stderr)\] (.*)$/;

// Regex for format without stream marker: "timestamp content" (defaults to stdout)
// Also handles stderr-only marker: "timestamp [stderr] content"
const LOG_LINE_WITHOUT_MARKER = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z) (.*)$/;

// Check for stderr prefix in content (worker format)
const STDERR_PREFIX = /^\[stderr\] /;

/**
 * Parse a single log line string into a LogLine object
 * Returns null if the line doesn't match expected format
 */
export function parseLogLine(line: string): LogLine | null {
  // Try format with explicit stream marker first
  const matchWithMarker = line.match(LOG_LINE_WITH_MARKER);
  if (matchWithMarker) {
    return {
      timestamp: matchWithMarker[1],
      stream: matchWithMarker[2] as 'stdout' | 'stderr',
      line: matchWithMarker[3],
    };
  }

  // Try format without stream marker
  const matchWithoutMarker = line.match(LOG_LINE_WITHOUT_MARKER);
  if (matchWithoutMarker) {
    const content = matchWithoutMarker[2];

    // Check if content has [stderr] prefix (worker format)
    if (STDERR_PREFIX.test(content)) {
      return {
        timestamp: matchWithoutMarker[1],
        stream: 'stderr',
        line: content.replace(STDERR_PREFIX, ''),
      };
    }

    // Default to stdout
    return {
      timestamp: matchWithoutMarker[1],
      stream: 'stdout',
      line: content,
    };
  }

  return null;
}

/**
 * Parse raw log content (multiple lines) into LogLine array
 * Filters out empty lines and lines that don't match expected format
 */
export function parseLogContent(content: string): LogLine[] {
  const lines = content.split('\n');
  const result: LogLine[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const parsed = parseLogLine(trimmed);
    if (parsed) {
      result.push(parsed);
    }
  }

  return result;
}
